import { Type, type Static } from "@sinclair/typebox";

/**
 * A trip type's id (docs/specs/trip-types.md): an admin-chosen slug such as `go`, `comfort-plat` or
 * `envios-carro`. Staff create types in admin; the wire still calls it `category`.
 */
export const RideCategory = Type.String({ $id: "RideCategory", pattern: "^[a-z0-9][a-z0-9-]{1,31}$" });
export type RideCategory = Static<typeof RideCategory>;
export const TRIP_TYPE_ID_PATTERN = /^[a-z0-9][a-z0-9-]{1,31}$/;

export const TripStatus = Type.Union(
  [
    Type.Literal("awaiting_payment"),
    /** Agendar: paid, waiting for `scheduledFor` minus the dispatch lead. */
    Type.Literal("scheduled"),
    Type.Literal("searching"),
    Type.Literal("driver_assigned"),
    Type.Literal("driver_arrived"),
    Type.Literal("in_progress"),
    Type.Literal("completed"),
    Type.Literal("cancelled"),
    Type.Literal("no_driver"),
    Type.Literal("payment_expired"),
  ],
  { $id: "TripStatus" },
);
export type TripStatus = Static<typeof TripStatus>;

export const DriverStatus = Type.Union(
  [Type.Literal("offline"), Type.Literal("available"), Type.Literal("offered"), Type.Literal("on_trip")],
  { $id: "DriverStatus" },
);
export type DriverStatus = Static<typeof DriverStatus>;

export const CancelledBy = Type.Union(
  [Type.Literal("rider"), Type.Literal("driver"), Type.Literal("system"), Type.Literal("staff")],
  { $id: "CancelledBy" },
);
export type CancelledBy = Static<typeof CancelledBy>;

export const LatLng = Type.Object({ lat: Type.Number(), lng: Type.Number() }, { $id: "LatLng" });
export type LatLng = Static<typeof LatLng>;

export const Place = Type.Object(
  { location: LatLng, address: Type.String(), placeId: Type.Optional(Type.String()) },
  { $id: "Place" },
);
export type Place = Static<typeof Place>;

/** A place sent by an app (pickup, dropoff, destination filter). */
export const PlaceInput = Type.Object({ address: Type.String({ minLength: 1, maxLength: 300 }), location: Type.Object({ lat: Type.Number({ minimum: -90, maximum: 90 }), lng: Type.Number({ minimum: -180, maximum: 180 }) }), placeId: Type.Optional(Type.String()) }, { $id: "PlaceInput" });

/** Money is always integer centavos (BRL). */
export const Cents = Type.Integer({ minimum: 0, $id: "Cents" });

/**
 * Pix is paid before dispatch; card (Carteira) is charged after the trip; cash ("Pagar na hora") is
 * paid to the driver at the end, in cash or by a Pix shown on the driver's phone.
 */
export const PaymentMethod = Type.Union([Type.Literal("pix"), Type.Literal("card"), Type.Literal("cash")], { $id: "PaymentMethod" });
export type PaymentMethod = Static<typeof PaymentMethod>;

/** A delivery (Envios) is a trip with `kind: "delivery"`; everything else is a ride. */
export const TripKind = Type.Union([Type.Literal("ride"), Type.Literal("delivery")], { $id: "TripKind" });
export type TripKind = Static<typeof TripKind>;
